import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckIcon } from '@/components/icons'
import { Spinner } from '@/components/Spinner'
import { OptOutDialog } from '@/components/programs/OptOutDialog'
import { StatusPill } from '@/components/programs/StatusPill'
import * as programsApi from '@/api/programs'
import * as v5 from '@/api/v5'
import { addressLabel } from '@/lib/addresses'
import { trackEvent } from '@/lib/analytics'
import {
  formatDayMonth,
  groupDevices,
  HEALTH_LABEL,
  NET_BEWUST_SLUG,
  participationAlert,
  participationBadge,
  participationTimeline,
  setupSummary,
  type TimelineState,
  type Tone,
} from '@/lib/programs'
import type { V5AddressSummary } from '@/types/api'
import type { Participation, ParticipationDevice } from '@/types/programs'

const BAR: Record<TimelineState, string> = {
  done: 'bg-dark-purple',
  current: 'bg-[linear-gradient(90deg,#6245DE_50%,#D5D3CE_50%)]',
  todo: 'bg-beige-2',
  stopped: 'bg-red',
}

const ALERT: Record<Tone, { box: string; dot: string }> = {
  primary: { box: 'bg-light-purple-3', dot: 'bg-dark-purple' },
  success: { box: 'bg-light-green', dot: 'bg-green' },
  warning: { box: 'bg-warning/15', dot: 'bg-warning' },
  error: { box: 'bg-red/10', dot: 'bg-red' },
  muted: { box: 'bg-beige', dot: 'bg-dark-blue/40' },
}

const DEVICE_DOT: Record<ParticipationDevice['health'], string> = {
  online: 'bg-green',
  stale: 'bg-warning',
  offline: 'bg-red',
  never: 'bg-red',
  unknown: 'bg-beige-2',
}

const DEVICE_TEXT: Record<ParticipationDevice['health'], string> = {
  online: 'text-green',
  stale: 'text-warning',
  offline: 'text-red',
  never: 'text-red',
  unknown: 'text-dark-blue/50',
}

function Timeline({ participation }: { participation: Participation }) {
  return (
    <ol className="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-4">
      {participationTimeline(participation).map((step) => (
        <li key={step.title}>
          <span className={`block h-1.5 rounded-full ${BAR[step.state]}`} aria-hidden="true" />
          <p className={`mt-2.5 text-15 font-bold ${step.state === 'stopped' ? 'text-red' : ''}`}>{step.title}</p>
          <p className="text-13 text-dark-blue/60">{step.detail}</p>
        </li>
      ))}
    </ol>
  )
}

function Setup({ participation }: { participation: Participation }) {
  const summary = setupSummary(participation)
  return (
    <details className="group rounded-20 bg-beige">
      <summary className="flex cursor-pointer list-none items-center gap-4 px-5 py-4 [&::-webkit-details-marker]:hidden">
        {summary.ok ? (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-light-green">
            <CheckIcon className="size-4" />
          </span>
        ) : (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-warning/15 text-15 font-bold text-warning">
            !
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-base font-bold">{summary.title}</span>
          <span className="block text-13 text-dark-blue/60">{summary.counts}</span>
        </span>
        <span className="text-15 font-semibold text-dark-purple">
          <span className="group-open:hidden">Bekijk</span>
          <span className="hidden group-open:inline">Verberg</span>
        </span>
      </summary>
      <div className="grid gap-2.5 px-4 pb-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        {groupDevices(participation.devices).map((group) => (
          <div
            key={group.role}
            className={`rounded-2xl bg-white px-4 py-3.5 ${group.issue ? 'ring-2 ring-inset ring-warning' : ''}`}
          >
            <div className="flex items-center gap-2">
              {group.issue ? (
                <span className="flex size-6 items-center justify-center rounded-full bg-warning/15 text-13 font-bold text-warning">
                  !
                </span>
              ) : (
                <span className="flex size-6 items-center justify-center rounded-full bg-light-green">
                  <CheckIcon className="size-3.5" />
                </span>
              )}
              <span className="text-11 font-bold uppercase tracking-wider text-dark-blue/60">{group.label}</span>
            </div>
            <ul className="mt-2 flex flex-col gap-2">
              {group.devices.map((d) => (
                <li key={d.id}>
                  <p className="text-15 font-bold">{d.name ?? 'Onbekend apparaat'}</p>
                  <p className={`flex items-center gap-1.5 text-13 font-semibold ${DEVICE_TEXT[d.health]}`}>
                    <span className={`size-[7px] rounded-full ${DEVICE_DOT[d.health]}`} aria-hidden="true" />
                    {HEALTH_LABEL[d.health]}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  )
}

function ParticipationCard({
  participation,
  address,
  onOptOut,
  onUndo,
  undoing,
  undoError,
}: {
  participation: Participation
  address: V5AddressSummary | undefined
  onOptOut: () => void
  onUndo: () => void
  undoing: boolean
  undoError: string
}) {
  const p = participation
  const badge = participationBadge(p)
  const alert = participationAlert(p)
  const live = p.status === 'pending' || p.status === 'active' || p.status === 'opting_out'
  return (
    <article className="card flex flex-col gap-7 p-6 md:p-10">
      <header className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <h2 className="text-26 font-bold leading-111 lg:text-[30px]">{p.program.name}</h2>
          {address && <p className="mt-1 text-15 text-dark-blue/60">{addressLabel(address)}</p>}
        </div>
        <StatusPill label={badge.label} tone={badge.tone} />
      </header>

      <Timeline participation={p} />

      {alert && (
        <div className={`flex gap-3 rounded-20 px-5 py-4 ${ALERT[alert.tone].box}`} role={alert.tone === 'warning' ? 'alert' : undefined}>
          <span className={`mt-2 size-2.5 shrink-0 rounded-full ${ALERT[alert.tone].dot}`} aria-hidden="true" />
          <div>
            <p className="text-base font-bold">{alert.title}</p>
            <p className="mt-0.5 text-15 leading-normal text-dark-blue/80">{alert.body}</p>
          </div>
        </div>
      )}

      {live && <Setup participation={p} />}

      {/* The month strip of peak-window charging arrives with monitoring (compliance data). */}

      {p.canOptOut && (
        <div className="flex justify-end">
          <button type="button" className="btn-text" onClick={onOptOut}>
            Stop met deelnemen
          </button>
        </div>
      )}

      {p.canUndoOptOut && p.endDate && (
        <div className="flex flex-col gap-3 border-t border-beige-2 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-15 text-dark-blue/70">
            Tot {formatDayMonth(p.endDate)} kun je je afmelding nog terugdraaien.
          </p>
          <button type="button" className="btn-cta shrink-0" onClick={onUndo} disabled={undoing}>
            {undoing ? 'Bezig…' : 'Toch blijven meedoen'}
          </button>
        </div>
      )}
      {undoError && <p className="text-15 font-semibold text-red">{undoError}</p>}
    </article>
  )
}

export function ProgramsOverviewPage() {
  const [participations, setParticipations] = useState<Participation[] | null>(null)
  const [addresses, setAddresses] = useState<V5AddressSummary[]>([])
  const [error, setError] = useState('')
  const [optingOut, setOptingOut] = useState<Participation | null>(null)
  const [optOutBusy, setOptOutBusy] = useState(false)
  const [optOutError, setOptOutError] = useState('')
  const [undoing, setUndoing] = useState<string | null>(null)
  const [undoError, setUndoError] = useState<{ id: string; message: string } | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    programsApi
      .listParticipations(controller.signal)
      .then(setParticipations)
      .catch(() => {
        if (!controller.signal.aborted) setError('We konden je programma’s niet ophalen. Probeer het later opnieuw.')
      })
    v5.getAddresses()
      .then((list) => !controller.signal.aborted && setAddresses(list))
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  const addressByUuid = useMemo(() => new Map(addresses.map((a) => [a.addressUuid, a])), [addresses])
  const replace = (updated: Participation) =>
    setParticipations((list) => list?.map((p) => (p.id === updated.id ? updated : p)) ?? null)

  function openOptOut(p: Participation) {
    setOptOutError('')
    setOptingOut(p)
    trackEvent('program_opt_out_open', { program: p.program.slug, from_status: p.status })
  }

  async function confirmOptOut() {
    if (!optingOut || optOutBusy) return
    setOptOutBusy(true)
    setOptOutError('')
    try {
      const updated = await programsApi.optOut(optingOut.id)
      trackEvent('program_opt_out_confirm', { program: optingOut.program.slug, from_status: optingOut.status })
      replace(updated)
      setOptingOut(null)
    } catch {
      setOptOutError('Stoppen is niet gelukt. Probeer het opnieuw.')
    } finally {
      setOptOutBusy(false)
    }
  }

  async function undo(p: Participation) {
    if (undoing) return
    setUndoing(p.id)
    setUndoError(null)
    try {
      replace(await programsApi.undoOptOut(p.id))
      trackEvent('program_opt_out_undo', { program: p.program.slug })
    } catch {
      setUndoError({ id: p.id, message: 'Terugdraaien is niet gelukt. Probeer het opnieuw.' })
    } finally {
      setUndoing(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-12">
      <nav className="mb-6 text-15 text-dark-blue/60">
        <Link to="/services" className="transition-all duration-300 hover:text-dark-blue">
          Diensten
        </Link>{' '}
        <span className="mx-1">/</span>
        <span className="text-dark-blue">Mijn programma’s</span>
      </nav>

      <h1 className="mb-6 text-42 font-bold leading-104 md:text-64">Mijn programma’s</h1>

      {error && <div className="card-beige p-5 text-base font-semibold text-red md:p-[30px]">{error}</div>}

      {!error && participations === null && (
        <div className="flex items-center gap-3 py-10 text-base text-dark-blue/60">
          <Spinner /> Programma’s laden…
        </div>
      )}

      {participations?.length === 0 && (
        <div className="card flex flex-col gap-4 p-6 md:p-10">
          <p className="text-base leading-normal text-dark-blue/70">Je doet nog niet mee aan een programma.</p>
          <Link to={`/programs/${NET_BEWUST_SLUG}`} className="btn-cta self-start">
            Bekijk Net bewust thuis laden
          </Link>
        </div>
      )}

      {participations && participations.length > 0 && (
        <div className="flex flex-col gap-5">
          {participations.map((p) => (
            <ParticipationCard
              key={p.id}
              participation={p}
              address={addressByUuid.get(p.addressUuid)}
              onOptOut={() => openOptOut(p)}
              onUndo={() => undo(p)}
              undoing={undoing === p.id}
              undoError={undoError?.id === p.id ? undoError.message : ''}
            />
          ))}
        </div>
      )}

      {optingOut && (
        <OptOutDialog
          participation={optingOut}
          busy={optOutBusy}
          error={optOutError}
          onConfirm={confirmOptOut}
          onCancel={() => setOptingOut(null)}
        />
      )}
    </div>
  )
}
